import { WSText } from "@wingspanhq/fe-component-library";
import { RequirementType } from "@wingspanhq/payments/dist/interfaces";
import { InsurancePolicyType } from "@wingspanhq/wingspan-api-sdk";
import React from "react";
import { useHistory } from "react-router-dom";
import {
  getPayerRequirementDetailUrl,
  PAYERS_REQUIREMENTS_PATH
} from "../../../../Payers/paths";
import { getRequirementDefinitionName } from "../../../../RequirementDefinitions/utils";
import { NotificationWithGroup } from "../../../selectors/selectorAlerts";
import { NotificationHandleType } from "../../Notifications";
import { selectorPayerNameFromContext } from "../../../selectors/selectorPayerNameFromContext";
export const GROUP_THRESHOLD = 2;

export type GenericRequirementNotificationContext = {
  payer: {
    payerId: string;
    payerName: string;
    logoFileId?: string;
  };
  requirement: {
    requirementId: string;
    name: string;
    dataSourceId: string;
    requirementType: RequirementType;
    dataSourceMetadata: {
      policyType?: InsurancePolicyType[];
    };
  };
};

export const getRequirementNameOrReadableType = (
  notification: NotificationWithGroup<GenericRequirementNotificationContext>
): string => {
  const requirement = notification.context?.requirement;
  return (
    requirement?.name ||
    getRequirementDefinitionName({
      type: requirement?.requirementType,
      ...requirement?.dataSourceMetadata
    })
  );
};

export const getGroupedRequirementNames = (
  notifications: NotificationWithGroup<GenericRequirementNotificationContext>
): string => {
  const completeGeneralRequirements: RequirementType[] = [];

  return (
    notifications.group
      .filter(notificaiton => {
        const requirementType =
          notificaiton.context?.requirement?.requirementType;

        switch (requirementType) {
          case RequirementType.Registration:
          case RequirementType.TaxVerification:
          case RequirementType.PayoutMethod:
            if (!completeGeneralRequirements.includes(requirementType)) {
              completeGeneralRequirements.push(requirementType);

              return true;
            }

            return false;

          default:
            return true;
        }
      })
      .map(g => g.context?.requirement?.name)
      // A notification whose context the API omitted contributes no name; drop it
      // rather than joining an empty slot into the list.
      .filter(Boolean)
      .join(", ")
  );
};

export const createRequirementAction = (
  notification: NotificationWithGroup<GenericRequirementNotificationContext>,
  history: ReturnType<typeof useHistory>
) => {
  const isGrouped = notification.group.length >= GROUP_THRESHOLD;

  return {
    text: isGrouped ? "View requirements" : "View requirement",
    onClick: () => {
      if (isGrouped) {
        history.push(PAYERS_REQUIREMENTS_PATH);
      } else {
        history.push(
          getPayerRequirementDetailUrl(
            notification.context?.payer?.payerId,
            notification.context?.requirement?.requirementId
          )
        );
      }
    }
  };
};

export const createRequirementTitle = (
  notification: NotificationWithGroup<GenericRequirementNotificationContext>,
  status: string
): string => {
  const requirement = notification.context?.requirement;
  const isGrouped = notification.group.length >= GROUP_THRESHOLD;
  const readableType = getRequirementDefinitionName({
    type: requirement?.requirementType,
    ...requirement?.dataSourceMetadata
  });

  switch (status) {
    case NotificationHandleType.RequirementsApprovedPayee:
      return isGrouped
        ? "Multiple requirements approved by client"
        : `${readableType} is now complete`;
    case NotificationHandleType.RequirementsRejectedPayee:
      return isGrouped ? "Multiple requirements rejected" : readableType;
    case NotificationHandleType.RequirementsAwaitingReviewPayee:
      return isGrouped
        ? "Multiple requirements awaiting clients"
        : readableType;
    case NotificationHandleType.RequirementsReRequestedPayee:
      return isGrouped ? "Multiple requirements re-requested" : readableType;
    case NotificationHandleType.RequirementsCompletePayee:
      return isGrouped
        ? "Multiple requirements complete"
        : `${readableType} is now complete`;
    default:
      return readableType;
  }
};

export const createRequirementChildren = (
  notification: NotificationWithGroup<GenericRequirementNotificationContext>,
  status: string
): React.ReactNode => {
  const isGrouped = notification.group.length >= GROUP_THRESHOLD;
  const requirementNameOrReadableType =
    getRequirementNameOrReadableType(notification);
  const groupedNames = getGroupedRequirementNames(notification);
  const payerName = selectorPayerNameFromContext(notification.context);

  switch (status) {
    case NotificationHandleType.RequirementsApprovedPayee:
      return isGrouped ? (
        <WSText.ParagraphSm color="gray600">
          {groupedNames} requirement was approved by client, and is now
          complete.
        </WSText.ParagraphSm>
      ) : (
        <WSText.ParagraphSm color="gray600">
          {requirementNameOrReadableType} requirement was approved by client,{" "}
          <b>{payerName}</b>, and is now complete.
        </WSText.ParagraphSm>
      );
    case NotificationHandleType.RequirementsRejectedPayee:
      return isGrouped ? (
        <WSText.ParagraphSm color="gray600">
          {groupedNames} requirement was rejected by clients.
        </WSText.ParagraphSm>
      ) : (
        <WSText.ParagraphSm color="gray600">
          {requirementNameOrReadableType} requirement was rejected by client,{" "}
          <b>{payerName}</b>.
        </WSText.ParagraphSm>
      );
    case NotificationHandleType.RequirementsAwaitingReviewPayee:
      return isGrouped ? (
        <WSText.ParagraphSm color="gray600">
          {groupedNames} are now awaiting clients. There is no additional action
          required at the moment. You will be notified of the outcome and any
          next steps.
        </WSText.ParagraphSm>
      ) : (
        <WSText.ParagraphSm color="gray600">
          {requirementNameOrReadableType} is now awaiting client,{" "}
          <b>{payerName}</b>. There is no additional action required at the
          moment. You will be notified of the outcome and any next steps.
        </WSText.ParagraphSm>
      );
    case NotificationHandleType.RequirementsReRequestedPayee:
      return isGrouped ? (
        <WSText.ParagraphSm color="gray600">
          {groupedNames} requirement was re-requested by clients.
        </WSText.ParagraphSm>
      ) : (
        <WSText.ParagraphSm color="gray600">
          {requirementNameOrReadableType} requirement was re-requested by
          client, <b>{payerName}</b>.
        </WSText.ParagraphSm>
      );
    case NotificationHandleType.RequirementsCompletePayee:
      return isGrouped ? (
        <WSText.ParagraphSm color="gray600">
          {groupedNames} requirement was completed by you.
        </WSText.ParagraphSm>
      ) : (
        <WSText.ParagraphSm color="gray600">
          {requirementNameOrReadableType} requirement was completed by you.
        </WSText.ParagraphSm>
      );
    default:
      return "";
  }
};
